import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Button, Typography } from '@evocolabs/orkaui'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Header from '@/src/components/HeaderV2'
import { showPromotionModal } from '@/src/components/Promotion/promotionSlice'
import FourOhFour from '@/pages/404'
import { classes, Root } from '@/src/components/Blog/[blogTitle].module.js'
import { BlogFooter } from '@/src/components/Blog/BlogFooter.js'
import {
  BlogBanner,
  BlogBreadcrumb,
  SEO
} from '@/src/components/Blog/BlogHeader.js'
import { ShareBox } from '@/src/components/Blog/ShareBox.js'
import Footer from '@/src/components/Footer/Footer2'
import { useBackend } from '@/src/providers/BackendProvider'
import SchedulePhoneCall from '@/src/components/Product/OrkaTwo/SchedulePhoneCall'

const DEFAULT_BLOG_URL = '/HearingBlog'
const BLOG_IMG_URL = 'https://legacy-cdn.hiorka.com/imgs/blog/'
const SchedulePhoneCallBlogCard = () => <SchedulePhoneCall asBlogCard={true} />

export async function getStaticProps() {
  const res = await fetch(process.env.NEXT_PUBLIC_BASEURL + 'blog')
  const data = await res.json()
  return { props: { data }, revalidate: 600 }
}

export async function getStaticPaths() {
  const res = await fetch(process.env.NEXT_PUBLIC_BASEURL + 'blog')
  const data = await res.json()
  return {
    paths: data.data.map((d) => {
      return {
        params: {
          blogTitle: d.url_postfix
        }
      }
    }),
    fallback: false // or 'blocking' depending on your needs
  }
}

export default function BlogPage({ data }) {
  const theme = useTheme()
  const router = useRouter()
  const { blogTitle } = router.query
  const { blogIndex, blogInfo, postBlogInfo, preBlogInfo } = parseBlogInfo(
    blogTitle,
    data
  )
  const { patchBlogLike, emailSubscribe } = useBackend()

  const title = blogInfo.meta_title
  const { description } = blogInfo
  const usePC = useMediaQuery(theme.breakpoints.up('md'))
  const blogNotFound = blogIndex == null || blogInfo == null

  useEffect(() => {
    if (blogIndex < 0) {
      router.push(DEFAULT_BLOG_URL)
    }
  }, [data])

  if (blogNotFound) return <FourOhFour />
  if (blogIndex < 0) return null

  return (
    <>
      <Header isWhiteBg />
      <Root>
        <SEO
          title={title}
          description={description}
          imageUrl={`${BLOG_IMG_URL}shareImgs/${blogInfo.pic}`}
        />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <BlogBanner bannerUrl={blogInfo.banner} usePC={usePC} />
          <Box width={usePC ? '846px' : '92vw'}>
            <BlogBreadcrumb theme={blogInfo.theme} />
            <BlogTitle title={blogInfo.title} />
            <BlogInfo
              blogInfo={blogInfo}
              usePC={usePC}
              patchBlogLike={patchBlogLike}
            />
            <hr
              className={classes.line}
              style={{ marginTop: '32px', marginBottom: '30px' }}
            />
            <BlogContent content={blogInfo.content} />
            <ShareBox />
            <BlogNavigation
              usePC={usePC}
              preBlogInfo={preBlogInfo}
              postBlogInfo={postBlogInfo}
            />
          </Box>
          <BlogFooter
            usePC={usePC}
            emailSubscribe={emailSubscribe}
            theme={theme}
            blogTitle={blogTitle}
          />
        </Box>
        <Footer />
      </Root>
    </>
  )
}

const parseBlogInfo = (blogTitle, data) => {
  const blogIndex = data.data.findIndex(
    (item) => item.url_postfix === blogTitle
  )
  const index = { pre: 0, now: 0, post: 0 }
  index.now = blogIndex < 0 ? 0 : blogIndex
  index.pre = index.now > 0 ? index.now - 1 : 0
  const dataLength = data?.data?.length ?? 0
  index.post = index.now < dataLength - 1 ? index.now + 1 : index.now
  const blogInfo = data?.data[index.now]
  const postBlogInfo = data?.data[index.post]
  const preBlogInfo = data?.data[index.pre]
  return {
    blogIndex,
    blogInfo,
    postBlogInfo,
    preBlogInfo
  }
}

function BlogTitle({ title }) {
  return (
    <Box
      sx={{
        marginBottom: '16px'
      }}
    >
      <Typography
        variant="h2"
        dangerouslySetInnerHTML={{ __html: title }}
      ></Typography>
    </Box>
  )
}

function BlogInfo({ blogInfo, patchBlogLike }) {
  const { author, write_time, spend_time } = blogInfo
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '8px'
        }}
      >
        <Typography variant="Body3">Written by {author}</Typography>
        <Typography variant="body3" color="#4E4947">
          {write_time} - {spend_time} min read
        </Typography>
      </Box>

      <LikeButton
        blogInfo={blogInfo}
        patchBlogLike={patchBlogLike}
        end={false}
        sx={{
          '@media (max-width: 959px)': { display: 'none' },
          display: 'flex'
        }}
      />
    </div>
  )
}

const BlogDisclaimer = () => {
  return (
    <Box
      sx={{
        marginBottom: '24px'
      }}
    >
      <Typography variant="body3" color="#756F6E">
        All content and information on this website is for informal and
        educational purposes only, nothing contained herein shall constitute
        medical advice, and does not establish any patient-client relationship
        by your use and access of this blog.
      </Typography>
    </Box>
  )
}

function BlogContent({ content }) {
  const dispatch = useDispatch()
  const PromotionButton = ({ id, ...restProps }) => (
    <Button
      id={id}
      {...restProps}
      onClick={() => {
        dispatch(showPromotionModal())
      }}
    />
  )

  return (
    <Root>
      <Markdown
        style={{ fontSize: '18px' }}
        options={{
          overrides: {
            Box,
            Typography,
            PromotionButton,
            Button,
            Link,
            SchedulePhoneCall: {
              component: SchedulePhoneCallBlogCard
            }
          }
        }}
      >
        {content}
      </Markdown>
      <hr className={classes.line} style={{ marginTop: '30px' }} />
      <BlogDisclaimer />
    </Root>
  )
}

function BlogNavigation({ usePC, preBlogInfo, postBlogInfo }) {
  return (
    <Box
      display="flex"
      flexDirection={usePC ? 'column' : 'column'}
      justifyContent="space-between"
      gap={'40px'}
    >
      <div
        style={
          usePC
            ? {
                width: '100%',
                display: 'flex',
                gap: '16px',
                flexDirection: 'column'
              }
            : { width: '100%', height: 'auto', marginBottom: '24px' }
        }
      >
        <Typography variant="T2" color="#26201F">
          Previous article
        </Typography>
        <Link href={`/blogPage/${preBlogInfo.url_postfix}`}>
          <MoreBlogCard blogInfo={preBlogInfo} usePC={usePC} />
        </Link>
      </div>
      <div
        style={
          usePC
            ? {
                width: '100%',
                marginBottom: '20px',
                display: 'flex',
                gap: '16px',
                flexDirection: 'column'
              }
            : { width: '100%', height: 'auto', marginBottom: '24px' }
        }
      >
        <Typography variant="T2" color="#26201F">
          Next article
        </Typography>
        <Link href={`/blogPage/${postBlogInfo.url_postfix}`}>
          <MoreBlogCard blogInfo={postBlogInfo} usePC={usePC} />
        </Link>
      </div>
    </Box>
  )
}
export function MoreBlogCard({ blogInfo, onClick, usePC }) {
  return (
    <Box
      style={
        usePC
          ? {
              display: 'flex',
              flexDirection: 'row',
              cursor: 'pointer'
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              height: 'auto'
            }
      }
      onClick={onClick}
    >
      <div>
        <div
          style={{
            backgroundImage: `url(https://legacy-cdn.hiorka.com/imgs/blog/shareImgs/${blogInfo.pic})`,
            backgroundPosition: 'center',
            position: 'relative',
            overflow: 'hidden',
            width: usePC ? '324px' : '100%',
            paddingTop: '60%',
            backgroundSize: 'cover',
            borderRadius: '12px',
            marginRight: usePC ? '24px' : '0px',
            marginTop: usePC ? '0px' : '16px',
            marginBottom: usePC ? '0px' : '16px'
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Typography variant="t2" color="#4E4947">
          {blogInfo.theme.toUpperCase()}
        </Typography>
        <Typography
          variant="h4"
          color="#26201F"
          dangerouslySetInnerHTML={{ __html: blogInfo.title }}
        ></Typography>
      </div>
    </Box>
  )
}

function LikeButton({ blogInfo, patchBlogLike, end }) {
  const [likeClick, setLikeClick] = useState(false)
  const [likes, setLikes] = useState(blogInfo?.like ?? 0)
  // get like status when component mounted
  useEffect(() => {
    const likedStatus =
      JSON.parse(localStorage.getItem(`like_${blogInfo.url_postfix}`) || 0) ||
      false
    setLikeClick(likedStatus || false)
  }, [blogInfo.title])

  const handleLike = async (isLiked) => {
    try {
      const newLikes = isLiked ? likes + 1 : likes - 1
      setLikes(newLikes)
      setLikeClick(isLiked)
      const info = {
        target_blog_title: blogInfo.title,
        like: isLiked
      }
      await patchBlogLike(info)

      // update localstorage like status
      localStorage.setItem(
        `like_${blogInfo.url_postfix}`,
        JSON.stringify(isLiked)
      )
    } catch (error) {
      console.error('Error updating likes:', error)
      // restore like status
      setLikes(likes)
      setLikeClick(!isLiked)
    }
  }

  const startStyle = { display: 'flex', alignItems: 'center' }
  const endStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }

  return (
    <div style={end ? endStyle : startStyle}>
      {likeClick ? (
        <FavoriteIcon
          className={classes.favoriteClick}
          onClick={() => handleLike(false)}
        />
      ) : (
        <FavoriteBorderIcon
          className={classes.favorite}
          onClick={() => handleLike(true)}
        />
      )}
      <Typography
        style={{ fontSize: '14px', color: '#636363', marginLeft: '4px' }}
      >
        {likes}
      </Typography>
    </div>
  )
}
